import React, { useState } from 'react';
import styled from 'styled-components';
import StyledButton from '../common/StyledButton';
import ToastContainer from '../common/ToastContainer';
import { apiService, ApiException, type BookingRequestPayload } from '../../services/api';
import { useToast } from '../../hooks/useToast';

interface FormValues {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
  dateTime: string;
  message: string;
}

interface FormErrors {
  [key: string]: string;
}

interface SubmissionState {
  error: string | null;
  isSubmitting: boolean;
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-weight: 500;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  transition: border-color var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const FormSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  background-color: white;
  transition: border-color var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const FormTextarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: border-color var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-sm);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  font-size: 0.8rem;
  margin-top: 4px;
`;

const ErrorMessageGlobal = styled.div`
  background-color: rgba(244, 67, 54, 0.1);
  color: #f44336;
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-sm);
  text-align: center;
  border: 1px solid rgba(244, 67, 54, 0.3);
`;

const ButtonContainer = styled.div`
  margin-top: var(--spacing-sm);
`;

const ContactForm: React.FC = () => {
  const initialValues: FormValues = {
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    dateTime: '',
    message: '',
  };

  const [formValues, setFormValues] = useState<FormValues>(initialValues);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submissionState, setSubmissionState] = useState<SubmissionState>({
    error: null,
    isSubmitting: false,
  });
  
  const { toasts, showToast, hideToast } = useToast();

  const serviceOptions = [
    { value: 'Sélectionnez un service', label: 'Sélectionnez un service' },
    { value: 'Transferts Aéroports & Gares', label: 'Transferts Aéroports & Gares' },
    { value: 'Mise à Disposition', label: 'Mise à Disposition' },
    { value: 'Déplacements Professionnels', label: 'Déplacements Professionnels' },
    { value: 'Événements Spéciaux', label: 'Événements Spéciaux' },
    { value: 'Service Longue Distance', label: 'Service Longue Distance' },
    { value: 'Circuits Touristiques Privés', label: 'Circuits Touristiques Privés' },
  ];

  const validate = (values: FormValues): FormErrors => {
    const errors: FormErrors = {};
    
    if (!values.name.trim()) {
      errors.name = 'Le nom est requis';
    }
    
    if (!values.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
      errors.email = 'Adresse email invalide';
    }
    
    if (!values.phone.trim()) {
      errors.phone = 'Le numéro de téléphone est requis';
    } else if (!/^[0-9+\s()-]{8,15}$/i.test(values.phone)) {
      errors.phone = 'Numéro de téléphone invalide';
    }
    
    if (!values.message.trim()) {
      errors.message = 'Le message est requis';
    }
    
    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validate(formValues);
    setFormErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      setSubmissionState({
        error: null,
        isSubmitting: true,
      });
      
      try {
        const bookingData: BookingRequestPayload = {
          name: formValues.name,
          email: formValues.email,
          phone: formValues.phone,
          serviceType: formValues.serviceType || undefined,
          dateTime: formValues.dateTime || undefined,
          message: formValues.message,
        };

        const response = await apiService.createBookingRequest(bookingData);
        
        setSubmissionState({
          error: null,
          isSubmitting: false,
        });
        
        // Afficher le toast de succès
        showToast({
          message: 'Merci pour votre demande ! Nous vous contacterons dans les plus brefs délais.',
          type: 'success',
          bookingId: response.bookingId,
          duration: 6000,
        });
        
        // Réinitialiser le formulaire en cas de succès
        setFormValues(initialValues);
        setFormErrors({});
        
      } catch (error) {
        if (error instanceof ApiException) {
          // Erreur avec détails de validation
          if (error.details && error.details.length > 0) {
            const newErrors: FormErrors = {};
            error.details.forEach(detail => {
              newErrors[detail.field] = detail.message;
            });
            setFormErrors(newErrors);
          }
          
          setSubmissionState({
            error: error.message,
            isSubmitting: false,
          });
        } else {
          setSubmissionState({
            error: 'Une erreur inattendue s\'est produite. Veuillez réessayer.',
            isSubmitting: false,
          });
        }
      }
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onClose={hideToast} />
      <FormContainer onSubmit={handleSubmit}>
        {submissionState.error && (
          <ErrorMessageGlobal>
            {submissionState.error}
          </ErrorMessageGlobal>
        )}
      
      <FormRow>
        <FormGroup>
          <FormLabel htmlFor="name">Nom Complet*</FormLabel>
          <FormInput
            type="text"
            id="name"
            name="name"
            value={formValues.name}
            onChange={handleChange}
            placeholder="Votre nom"
          />
          {formErrors.name && <ErrorMessage>{formErrors.name}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="email">Email*</FormLabel>
          <FormInput
            type="email"
            id="email"
            name="email"
            value={formValues.email}
            onChange={handleChange}
            placeholder="Votre adresse email"
          />
          {formErrors.email && <ErrorMessage>{formErrors.email}</ErrorMessage>}
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel htmlFor="phone">Téléphone*</FormLabel>
          <FormInput
            type="tel"
            id="phone"
            name="phone"
            value={formValues.phone}
            onChange={handleChange}
            placeholder="Votre numéro de téléphone"
          />
          {formErrors.phone && <ErrorMessage>{formErrors.phone}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="serviceType">Type de Service</FormLabel>
          <FormSelect
            id="serviceType"
            name="serviceType"
            value={formValues.serviceType}
            onChange={handleChange}
          >
            {serviceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </FormGroup>
      </FormRow>
      
      <FormGroup>
        <FormLabel htmlFor="dateTime">Date et Heure (optionnel)</FormLabel>
        <FormInput
          type="datetime-local"
          id="dateTime"
          name="dateTime"
          value={formValues.dateTime}
          onChange={handleChange}
        />
      </FormGroup>
      
      <FormGroup>
        <FormLabel htmlFor="message">Message / Détails de la demande*</FormLabel>
        <FormTextarea
          id="message"
          name="message"
          value={formValues.message}
          onChange={handleChange}
          placeholder="Décrivez votre besoin (adresse de départ/arrivée, nombre de passagers, etc.)"
        />
        {formErrors.message && <ErrorMessage>{formErrors.message}</ErrorMessage>}
      </FormGroup>
      
      <ButtonContainer>
        <StyledButton 
          type="submit" 
          variant="primary" 
          size="large" 
          fullWidth 
          disabled={submissionState.isSubmitting}
        >
          {submissionState.isSubmitting ? 'Envoi en cours...' : 'Envoyer Votre Demande'}
        </StyledButton>
      </ButtonContainer>
    </FormContainer>
    </>
  );
};

export default ContactForm; 